/**
 * NowExpress — Availability contract (nx-availability.js)
 *
 * Shared, UI-agnostic helper for the ops "disable on storefront" mechanism.
 * Reads the additive fields the Portal adds to GET /api/public/lab/branches:
 *
 *   available          {boolean}  false => render greyed out and unselectable
 *   unavailableReason  {string}   short patient-facing note, "" when available
 *   unavailableUntil   {string}   ISO-8601 UTC, null = indefinite. DISPLAY ONLY —
 *                                 never compute availability from it, the server
 *                                 has already resolved the window.
 *
 * This file owns NO markup and NO styling. Each picker keeps its own DOM and calls
 * decorate() to get consistent semantics. Rendering differs per product flow; the
 * decision of whether something is selectable does not.
 *
 * Usage:
 *   var NXA = window.NXAvailability;
 *   NXA.attach(myBranchObject, rawPayloadRow);   // in the adapter
 *   NXA.decorate(buttonEl, myBranchObject);      // when rendering the option
 *   if (!NXA.isAvailable(b)) { ... }             // anywhere a decision is made
 */
(function () {
  'use strict';

  var DEFAULT_REASON = 'Temporarily unavailable';

  /** Absent `available` means available — an older Portal must not grey everything out. */
  function normalise(raw) {
    if (!raw) return { available: true, unavailableReason: '', unavailableUntil: null };

    var available = (raw.available === undefined || raw.available === null) ? true : !!raw.available;
    var until = null;
    if (!available && raw.unavailableUntil) {
      var parsed = new Date(raw.unavailableUntil);
      if (!isNaN(parsed.getTime())) until = parsed;
    }

    return {
      available: available,
      unavailableReason: available ? '' : String(raw.unavailableReason || DEFAULT_REASON),
      unavailableUntil: until
    };
  }

  window.NXAvailability = {
    normalise: normalise,

    /**
     * Copies the three normalised fields onto whatever object an adapter builds.
     * One line per adapter — the whole point of this file.
     */
    attach: function (target, raw) {
      if (!target) return target;
      var a = normalise(raw);
      target.available = a.available;
      target.unavailableReason = a.unavailableReason;
      target.unavailableUntil = a.unavailableUntil;
      return target;
    },

    /** The single predicate. Anything unknown is treated as available. */
    isAvailable: function (o) {
      return !o || o.available !== false;
    },

    /** "Temporarily closed · reopens Aug 12" — for the picker's existing sub-line slot. */
    label: function (o) {
      if (this.isAvailable(o)) return '';
      var text = o.unavailableReason || DEFAULT_REASON;
      if (o.unavailableUntil) {
        try {
          text += ' · reopens ' + o.unavailableUntil.toLocaleDateString('en-PH', {
            month: 'short', day: 'numeric', timeZone: 'Asia/Manila'
          });
        } catch (e) { /* older browsers: reason alone is fine */ }
      }
      return text;
    },

    /**
     * Applies selection semantics only — the section's own CSS does the greying,
     * keyed off [data-nx-unavailable].
     */
    decorate: function (el, o) {
      if (!el) return el;

      if (this.isAvailable(o)) {
        el.removeAttribute('disabled');
        el.removeAttribute('aria-disabled');
        el.removeAttribute('data-nx-unavailable');
        return el;
      }

      el.setAttribute('disabled', 'disabled');
      el.setAttribute('aria-disabled', 'true');
      el.setAttribute('data-nx-unavailable', '');
      el.setAttribute('title', this.label(o));
      return el;
    },

    /**
     * First selectable index, keeping `preferred` when it is still available.
     * Returns -1 when nothing in the list can be picked.
     */
    pickFallbackIndex: function (list, preferred) {
      list = list || [];
      if (typeof preferred === 'number' && list[preferred] && this.isAvailable(list[preferred])) {
        return preferred;
      }
      for (var i = 0; i < list.length; i++) {
        if (this.isAvailable(list[i])) return i;
      }
      return -1;
    },

    /** Tolerant id lookup — persisted ids come back from storage as strings. */
    resolveById: function (list, id) {
      if (id === null || id === undefined || id === '') return null;
      list = list || [];
      for (var i = 0; i < list.length; i++) {
        if (list[i] && String(list[i].id) === String(id)) return list[i];
      }
      return null;
    },

    /** True when every entry is unavailable (an entire lab is closed, say). */
    noneAvailable: function (list) {
      list = list || [];
      if (!list.length) return false;
      for (var i = 0; i < list.length; i++) {
        if (this.isAvailable(list[i])) return false;
      }
      return true;
    }
  };
})();
